class ProductRecommendationObserver {
  constructor(options = {}) {
    this.targetSelector = '[data-ss-recs]';
    this.productItemSelector = '[data-ss-product-recommendation="true"]';
    this.onProductsLoaded = options.onProductsLoaded || null;
    this.onError = options.onError || null;
    this.processedElements = new WeakSet(); // Track processed elements
    this.observer = null;
    this.isProcessing = false;
    
    // Start observing immediately
    this.startObserving();
  }

  /**
   * Start observing the DOM for target elements
   */
  startObserving() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // Process any existing elements
    this.processExistingElements();

    // Create MutationObserver to watch for new elements
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is our target
              const targetElement = node.matches && node.matches(this.targetSelector) 
                ? node 
                : node.querySelector && node.querySelector(this.targetSelector);
              
              if (targetElement) {
                this.handleSingleElement(targetElement);
              }
            }
          }
        }
      }
    });

    // Start observing
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process existing elements that are already in the DOM
   */
  processExistingElements() {
    const existingElements = document.querySelectorAll(this.targetSelector);
    
    if (existingElements.length > 0) {
      // Process each element individually to avoid conflicts
      existingElements.forEach((element, index) => {
        setTimeout(() => {
          this.handleSingleElement(element);
        }, index * 100); // Small delay between elements to prevent conflicts
      });
    }
  }

  /**
   * Handle a single element
   */
  async handleSingleElement(element) {
    // Check if already processed
    if (this.processedElements.has(element)) {
      return;
    }

    // Mark as processed immediately to prevent reprocessing
    this.processedElements.add(element);

    try {
      // Extract handles from the element
      const handleData = this.extractHandlesWithElements(element);

      if (handleData.length > 0) {
        // Render products using the handles
        await this.renderProductsInPlace(handleData);
      } else {
        console.warn('No product handles found in the element');
      }
    } catch (error) {
      console.error('Error processing element:', error);
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  /**
   * Extract product handles along with their corresponding DOM elements
   */
  extractHandlesWithElements(element) {
    const productItems = element.querySelectorAll(this.productItemSelector);
    const handleData = [];

    productItems.forEach(item => {
      const handleAttr = item.getAttribute('data-handle');
      if (handleAttr) {
        // Check if this item was already processed (has product card)
        if (item.querySelector('.appended-product-card')) {
          return;
        }

        const handle = handleAttr.replace(/^\/products\//, '');
        if (handle) {
          handleData.push({
            handle: handle,
            element: item,
            originalHandle: handleAttr
          });
        }
      }
    });

    return handleData;
  }

  /**
   * Create skeleton loading div
   */
  createSkeletonDiv() {
    const skeleton = document.createElement('div');
    skeleton.className = 'product-card product-card--skeleton product-card--skeleton-recs';
    skeleton.setAttribute('data-loading', 'true');
    skeleton.innerHTML = `
      <div class="skeleton"></div>
    `;
    return skeleton;
  }

   

  /**
   * Render products in place within their respective divs
   */
  async renderProductsInPlace(handleData) {
    try {

      // Show loading state in each div
      handleData.forEach(data => {
        const skeleton = this.createSkeletonDiv();
        data.element.appendChild(skeleton);
      });

      // Create URLs for section rendering
      const urlStrings = handleData.map(data => {
        return `${Shopify.routes.root}products/${data.handle}?section_id=product-card`;
      });


      // Fetch all products in parallel
      const responses = await Promise.all(
        urlStrings.map((url, index) =>
          fetch(url)
            .then(response => response.text())
            .then(text => ({ text, index, handle: handleData[index].handle }))
            .catch(error => {
              console.error(`Failed to fetch ${url}:`, error);
              return { text: null, index, handle: handleData[index].handle };
            })
        )
      );

      // Parse responses and append product cards to their respective divs
      const parser = new DOMParser();
      let successCount = 0;

      responses.forEach(response => {
        const data = handleData[response.index];
        const targetDiv = data.element;

        // Remove loading skeleton
        const skeleton = targetDiv.querySelector('[data-loading="true"]');
        if (skeleton) {
          skeleton.remove();
        }

        if (response.text) {
          try {
            const doc = parser.parseFromString(response.text, 'text/html');
            const productCard = doc.querySelector('.product-card');

            if (productCard) {
              // Create a wrapper for the product card
              const cardWrapper = document.createElement('div');
              cardWrapper.className = 'appended-product-card';
              cardWrapper.style.cssText = 'margin-top: 10px;';
              cardWrapper.appendChild(productCard);
              
              // Append the product card to the existing div
              targetDiv.appendChild(cardWrapper);
              successCount++;
              
            } else {
              console.warn(`No product card found for handle: ${data.handle}`);
              this.appendErrorMessage(targetDiv, 'Product not found');
            }
          } catch (parseError) {
            console.error(`Error parsing response for handle ${data.handle}:`, parseError);
            this.appendErrorMessage(targetDiv, 'Error loading product');
          }
        } else {
          console.error(`Failed to fetch product for handle: ${data.handle}`);
          this.appendErrorMessage(targetDiv, 'Failed to load product');
        }
      });


      // Call callback if provided
      if (this.onProductsLoaded) {
        const successfulHandles = handleData
          .filter((_, index) => responses[index].text !== null)
          .map(data => data.handle);
        this.onProductsLoaded(successfulHandles, handleData.length);
      }

    } catch (error) {
      console.error('Error rendering products:', error);
      
      // Remove all loading skeletons and show error
      handleData.forEach(data => {
        const skeleton = data.element.querySelector('[data-loading="true"]');
        if (skeleton) {
          skeleton.remove();
        }
        this.appendErrorMessage(data.element, 'Error loading products');
      });
      
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  /**
   * Append error message to a target div
   */
  appendErrorMessage(targetDiv, message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'product-load-error';
    errorDiv.style.cssText = 'margin-top: 10px; padding: 8px; background: #ffebee; color: #c62828; border-radius: 4px; font-size: 14px;';
    errorDiv.textContent = message;
    targetDiv.appendChild(errorDiv);
  }

  /**
   * Stop observing the DOM
   */
  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Get statistics about processed elements
   */
  getStats() {
    const allElements = document.querySelectorAll(this.targetSelector);
    const processedCount = Array.from(allElements).filter(el => this.processedElements.has(el)).length;
    
    return {
      total: allElements.length,
      processed: processedCount,
      pending: allElements.length - processedCount
    };
  }

  /**
   * Manually process all existing elements
   */
  processAllExisting() {
    // Clear processed tracking to force reprocessing
    this.processedElements = new WeakSet();
    this.processExistingElements();
  }

  /**
   * Destroy the observer and clean up
   */
  destroy() {
    this.stopObserving();
    this.processedElements = new WeakSet();
    this.onProductsLoaded = null;
    this.onError = null;
  }
}

// Usage:
const observer = new ProductRecommendationObserver({
  onProductsLoaded: (successfulHandles, totalCount) => {
  },
  onError: (error) => {
    console.error('❌ Observer error:', error);
  }
});
